<template>
  <div class="wrapper">
    <div id="map-container"></div>
    <h3 style="font-size: 20px; font-weight: 500;">{{ $t('company_info') }}</h3>
    <p>{{ $t('fill_required_fields') }}</p>
    <v-layout class="form-wrapper" wrap>
      <v-flex xs12>
        <CompanyAutocomplete
          :search-route="companySearchRoute"
          :label-text="$t('enter_company_name_or_code')"
          text-field="name"
          :loading="loadingClients"
          class="company-form-input"
          clearable
          @itemSelected="companySelected"
        />
      </v-flex>
    </v-layout>

    <v-form ref="form" @submit.prevent>
      <div v-if="loadingClients" class="text-xs-center my-4">
        <v-progress-circular color="primary" indeterminate/>
      </div>

      <template v-else-if="client">
        <v-layout class="form-wrapper mt-1" wrap>
          <v-flex xs12 sm6>
            <v-text-field
              v-model="client.name"
              :error-messages="errors.name"
              :label="$t('company_name')"
              :rules="[required]"
              class="company-form-input"
              name="name"
              @blur="checkForErrors('name')"
            />
          </v-flex>
          <v-flex xs12 sm6>
            <v-text-field
              v-model="client.code"
              :error-messages="errors.code"
              :label="$t('company_code')"
              :rules="[required]"
              class="company-form-input"
              name="code"
              @blur="checkForErrors('code')"
            />
          </v-flex>
          <v-flex xs12 sm6>
            <v-text-field
              v-model="client.vat_payer_code"
              :error-messages="errors.vat_payer_code"
              :label="$t('vat_payer_code')"
              class="company-form-input"
              name="vat_payer_code"
              @blur="checkForErrors('vat_payer_code')"
            />
          </v-flex>
          <v-flex class="text-xs-center text-sm-right" align-self-center xs12 sm6>
            <AddressSearch
              @addressSelected="onAddressSelect"
              show-in-menu
            />
          </v-flex>
          <v-flex xs12 sm6>
            <v-text-field
              v-model="client.address_street"
              :error-messages="errors.address_street"
              :label="$t('street')"
              :rules="[required]"
              class="company-form-input"
              name="address_street"
              @input="setPostalCode(addressString)"
              @blur="checkForErrors('address_street')"
            />
          </v-flex>
          <v-flex xs12 sm6>
            <v-text-field
              v-model="client.address_nr"
              :error-messages="errors.address_nr"
              :label="$t('building_flat_no')"
              :rules="[required]"
              class="company-form-input"
              name="address_nr"
              @input="setPostalCode(addressString)"
              @blur="checkForErrors('address_nr')"
            />
          </v-flex>
          <v-flex xs12 sm6>
            <v-text-field
              v-model="client.address_post_code"
              :error-messages="errors.address_post_code"
              :label="$t('postal_code')"
              :rules="[postCode]"
              class="company-form-input"
              name="address_post_code"
              disabled
              @blur="checkForErrors('address_post_code')"
            />
          </v-flex>
          <v-flex xs12 sm6>
            <v-text-field
              v-model="client.address_city"
              :error-messages="errors.address_city"
              :label="$t('city')"
              :rules="[required]"
              class="company-form-input"
              name="address_city"
              @input="setPostalCode(addressString)"
              @blur="checkForErrors('address_city')"
            />
          </v-flex>
          <v-flex xs12 sm6>
            <v-text-field
              v-model="client.email"
              :error-messages="errors.email"
              :label="$t('email')"
              :rules="[required]"
              class="company-form-input"
              name="email"
              @blur="checkForErrors('email')"
            />
          </v-flex>
          <v-flex style="display: flex; align-items: center;" xs12 sm6>
            <template v-if="isExistingClient">
              <img src="../assets/icons/yes-check-2.svg" alt="Checkbox checked" class="px-2">
              <span style="font-size: 20px">{{ $t('existing_cramo_client') }}</span>
            </template>
            <template v-else>
              <img src="../assets/icons/warning-icon.svg" alt="Warning" class="px-2">
              <span style="font-size: 20px">{{ $t('new_cramo_client') }} *</span>
            </template>
          </v-flex>
        </v-layout>

        <div v-if="!isExistingClient" class="mt-1" style="font-size: 20px; color: #B91E00">
          * {{ $t('client_not_found_message') }}
        </div>

        <h3 class="mt-3" style="font-size: 20px; font-weight: 500;">{{ contactPartHeader }}</h3>
        <!--<p>{{ $t('add_applying_person_info') }}</p>-->
        <v-layout class="form-wrapper mt-1" wrap>
          <v-flex xs12 sm6>
            <v-radio-group
              v-model="client.contacts[0].type"
              :rules="[required]">
              <v-radio :label="$t('i_am_company_manager')" value="manager"/>
              <v-radio :label="$t('i_am_authorized_person')" value="authorized_person"/>
              <v-radio v-if="isExistingClient" :label="$t('unauthorized_person')" value="other"/>
            </v-radio-group>
          </v-flex>
          <v-flex xs12 sm6>
            <v-layout wrap>
              <v-flex xs12>
                <v-text-field
                  v-model="client.contacts[0].email"
                  :label="$t('email_address')"
                  :error-messages="errors['contacts.0.email']"
                  :rules="[required]"
                  name="contacts.0.email"
                  @blur="checkForErrors('contacts.0.email')"
                />
              </v-flex>
              <v-flex sm4 xs12>
                <v-text-field
                  v-model="client.contacts[0].first_name"
                  :label="$t('first_name')"
                  :error-messages="errors['contacts.0.first_name']"
                  :rules="[required]"
                  name="contacts.0.first_name"
                  @blur="checkForErrors('contacts.0.first_name')"
                />
              </v-flex>
              <v-flex sm4 xs12>
                <v-text-field
                  v-model="client.contacts[0].last_name"
                  :label="$t('last_name')"
                  :error-messages="errors['contacts.0.last_name']"
                  :rules="[required]"
                  name="contacts.0.last_name"
                  @blur="checkForErrors('contacts.0.last_name')"
                />
              </v-flex>
              <v-flex sm4 xs12>
                <v-text-field
                  v-model="client.contacts[0].phone"
                  :label="$t('phone')"
                  :error-messages="errors['contacts.0.phone']"
                  :rules="[required]"
                  name="contacts.0.phone"
                  @blur="checkForErrors('contacts.0.phone')"
                />
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex sm6 xs12></v-flex>
          <v-flex sm6 xs12>
            <v-checkbox
              v-model="client.contacts[0].is_newsletter_recipient"
              :label="$t('i_want_to_receive_newsletters')"
              name="contacts.0.is_newsletter_recipient"
            />
          </v-flex>
        </v-layout>

        <template v-if="client.contacts[0].type !== 'other'">
          <div v-for="(contact, index) in client.contacts" :key="index">
            <EmployeeForm
              v-if="index > 0"
              :contact="contact"
              :index="index"
              :errors="errors"
            />
            <v-layout v-if="index > 0" class="form-wrapper" justify-end>
              <v-btn
                v-if="index === client.contacts.length - 1"
                slot="activator"
                :disabled="!isExistingClient && client.contacts.length > 10"
                color="primary"
                class="primary-btn mr-3"
                @click="addContact">
                {{ !isExistingClient && client.contacts.length > 10 ? $t('max_10_employees') : $t('add_another') }}
              </v-btn>

              <v-btn color="#FF7777" class="primary-btn" @click="removeContact(index)">
                {{ $t('remove') }}
              </v-btn>
            </v-layout>
          </div>
          <v-layout v-if="client.contacts.length < 2" class="form-wrapper mt-1" justify-end>
            <v-btn color="primary" class="primary-btn" @click="addContact">
              {{ $t('authorize_employee') }}
            </v-btn>
          </v-layout>
        </template>

        <div class="text-xs-right mt-2">
          <v-btn color="primary" class="primary-btn" @click="onSubmit">
            {{ $t('continue') }}
          </v-btn>
        </div>
      </template>

    </v-form>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import EmployeeForm from '../components/EmployeeForm.vue';
import formMixin from '../mixins/form-mixin';
import googleMapsPlacesMixin from '../mixins/google-maps-places-mixin';
import AddressSearch from '../components/AddressSearch.vue';
import CompanyAutocomplete from '../components/CompanyAutocomplete.vue';

export default {
  name: 'CompanyForm',

  components: {
    CompanyAutocomplete,
    AddressSearch,
    EmployeeForm,
  },

  mixins: [formMixin, googleMapsPlacesMixin],

  data() {
    return {
      searchTerm: '',
      companySearchRoute: 'api/client-list/',
      items: [],
    };
  },

  computed: {
    ...mapState({
      isExistingClient: state => state.clients.isExisting,
      client: state => state.clients.companyFormItem,
      errors: state => state.clients.errors,
      clientRole: state => (state.clients.companyFormItem.contacts && state.clients.companyFormItem.contacts.length
        ? state.clients.companyFormItem.contacts[0].type // manager, authorized_person, other
        : null),
      loadingClients: state => state.clients.loadingClients,
      manager: state => state.clients.manager,
    }),

    contactPartHeader() {
      return this.isExistingClient
        ? this.$t('existing_client_contact_header')
        : this.$t('new_client_contact_header');
    },

    addressString() {
      return `${this.client.address_street} ${this.client.address_nr} ${this.client.address_city}`;
    },
  },

  created() {
    this.storeClientType('company');
  },

  methods: {
    ...mapActions({
      findClient: 'clients/find',
    }),

    ...mapMutations({
      storeClientType: 'clients/storeType',
      clearErrors: 'clients/clearErrors',
      addContact: 'clients/addContact',
      removeContact: 'clients/removeContact',
      setAddressFields: 'clients/setAddressFields',
      openSnackbar: 'ui/openSnackbar',
    }),

    onSubmit() {
      if (!this.$refs.form.validate()) {
        return;
      }
      if (this.clientRole === 'authorized_person') {
        this.$router.push({ name: 'warrant-upload-form' });
      } else {
        this.$router.push({ name: 'agreements' });
      }
    },

    checkForErrors(field) {
      if (this.errors[field].length) {
        this.clearErrors(field);
      }
    },

    onAddressSelect(val) {
      this.setAddressFields(val);
    },

    companySelected(company) {
      this.findClient(company).then(() => {
        if (!this.client.address_post_code) {
          this.setPostalCode(this.addressString);
        }
      });
    },
  },
};
</script>

<style scoped>
  .wrapper {
    padding: 0 36px;
    margin-bottom: 120px;
  }
</style>
